import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@shared/services/auth.service';
import { SpringPage } from '@shared/page';
import { ERROR_CONTENT_TYPE } from '../../enums/errors/ErrorContentTypes';
import { Audit } from '@shared/models/audit';
import { AbstractOrganizationService } from '@shared/services/abstractOrganizationService';

export interface PageTemplateModel {
  contentType: string | null;
  body: string | null;
}

export interface ChallengeTemplateModel {
  header: string | null;
  footer: string | null;
  backgroundColor: string | null;
}

export interface CustomPagesProfileModel extends CustomPagesProfileData, Audit {
  uid: string;
}
export interface CustomPagesProfileData {
  name: string;
  comment: string | null;
  blockedTemplate: PageTemplateModel | null;
  originErrorTemplate: PageTemplateModel | null;
  challengeTemplate: ChallengeTemplateModel | null;
}

export interface DefaultTemplates {
  blockedTemplate: PageTemplateModel;
  originErrorTemplate: PageTemplateModel;
  challengeTemplate: ChallengeTemplateModel;
}

export const DEFAULT_TEMPLATE_KEYS = {
  blockedBody: 'ErrorHandler.default.blockedBody',
  originErrorBody: 'ErrorHandler.default.originErrorBody',
  challengeHeader: 'ErrorHandler.default.challengeHeader',
  challengeFooter: 'ErrorHandler.default.challengeFooter',
} as const;

export const DEFAULT_CHALLENGE_BACKGROUND_COLOR = '#f0f0f0';

export function buildDefaultTemplates(translate: TranslateService): DefaultTemplates {
  return {
    blockedTemplate: {
      contentType: ERROR_CONTENT_TYPE.PLAIN,
      body: translate.instant(DEFAULT_TEMPLATE_KEYS.blockedBody),
    },
    originErrorTemplate: {
      contentType: ERROR_CONTENT_TYPE.PLAIN,
      body: translate.instant(DEFAULT_TEMPLATE_KEYS.originErrorBody),
    },
    challengeTemplate: {
      header: translate.instant(DEFAULT_TEMPLATE_KEYS.challengeHeader),
      footer: translate.instant(DEFAULT_TEMPLATE_KEYS.challengeFooter),
      backgroundColor: DEFAULT_CHALLENGE_BACKGROUND_COLOR,
    },
  };
}

@Injectable({ providedIn: 'root' })
export class OrganizationCustomPagesProfileService extends AbstractOrganizationService {
  constructor(
    auth: AuthService,
    private http: HttpClient,
    private translate: TranslateService,
  ) {
    super(auth);
  }

  getDefaultTemplates(): DefaultTemplates {
    return buildDefaultTemplates(this.translate);
  }

  removeCustomPagesProfiles(uid: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl()}/${uid}`);
  }

  getCustomPagesProfiles(param: {
    number: number;
    size: number;
    search?: string;
  }): Observable<SpringPage<CustomPagesProfileModel>> {
    let params = new HttpParams().set('page', param.number).set('size', param.size);
    const term = param.search?.trim();
    if (term) {
      params = params.set('search', term);
    }
    return this.http.get<SpringPage<CustomPagesProfileModel>>(this.baseUrl(), { params });
  }

  getCustomPagesProfilesById(uid: string): Observable<CustomPagesProfileModel | undefined> {
    return this.http
      .get<CustomPagesProfileModel | null>(`${this.baseUrl()}/${uid}`)
      .pipe(map((profile) => profile ?? undefined));
  }

  createCustomPagesProfiles(body: CustomPagesProfileData): Observable<CustomPagesProfileModel> {
    return this.http.post<CustomPagesProfileModel>(this.baseUrl(), body);
  }
  updateCustomPagesProfiles(uid: string, body: CustomPagesProfileData): Observable<CustomPagesProfileModel> {
    return this.http.put<CustomPagesProfileModel>(`${this.baseUrl()}/${uid}`, body);
  }

  private baseUrl(): string {
    return this.url('/custom-pages-profiles');
  }
}
